import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  STAGES, TEXT_MAX, RATING_VALUES, LOW_RATINGS, sectionsForStage, isShown, isRating, missingRequired, visibleAnswers,
  formSections, whyId,
} from "@/lib/betaReport/schema";
import { betaReportStrings } from "@/lib/betaReport/i18n";
import { useBetaReportWording } from "@/lib/betaReport/wording";
import { clientInfo } from "@/lib/betaReport/clientInfo";

// The Beta Report (ACCOUNT_TIER_RULES.md Beta Tester standards, 2026-10-30): the product
// owner's brief, five steps with conditional follow-ups, progress saved locally and on
// the server, and required before the tester's next interview.

const draftKey = (sessionId) => `mec_beta_report_draft_${sessionId}`;

export default function BetaReport() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ctx, setCtx] = useState(null);
  const [answers, setAnswers] = useState({});
  const [stage, setStage] = useState(0);
  const [showMissing, setShowMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const topRef = useRef(null);
  const wordingReady = useBetaReportWording(); // wording and questions published from Admin (2026-11-04)

  useEffect(() => {
    let alive = true;
    api.get(`/beta-report/${sessionId}`)
      .then(({ data }) => {
        if (!alive) return;
        let local = null;
        try { local = JSON.parse(localStorage.getItem(draftKey(sessionId)) || "null"); } catch { local = null; }
        const server = data.draft || {};
        const draft = local && Object.keys(local.answers || {}).length >= Object.keys(server.answers || {}).length ? local : server;
        setAnswers(draft.answers || {});
        setStage(Math.min(Number(draft.stage) || 0, STAGES.length - 1));
        setCtx(data);
      })
      .catch(() => alive && setFailed(true));
    return () => { alive = false; };
  }, [sessionId]);

  const lang = ctx?.interface_language || user?.preferred_language || "en";
  const s = betaReportStrings(lang);
  const formCtx = useMemo(() => ({ language: lang, questionCount: ctx?.question_count || 0 }), [lang, ctx]);
  const sections = formSections();
  const stageSections = sectionsForStage(STAGES[stage], sections);

  const saveDraft = useCallback((nextAnswers, nextStage) => {
    try { localStorage.setItem(draftKey(sessionId), JSON.stringify({ answers: nextAnswers, stage: nextStage })); } catch { /* blocked */ }
    api.put(`/beta-report/${sessionId}/draft`, { answers: nextAnswers, stage: nextStage }).catch(() => {});
  }, [sessionId]);

  const set = (id, value) => {
    setAnswers((prev) => {
      const next = { ...prev, [id]: value };
      try { localStorage.setItem(draftKey(sessionId), JSON.stringify({ answers: next, stage })); } catch { /* blocked */ }
      return next;
    });
  };

  const missing = missingRequired(stageSections, answers, formCtx);
  const go = (to) => {
    if (to > stage && missing.length) {
      setShowMissing(true);
      toast.error(s.ui.missing);
      return;
    }
    setShowMissing(false);
    setStage(to);
    saveDraft(answers, to);
    topRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const display = () => {
    const shown = visibleAnswers(answers, formCtx);
    const ENGLISH = betaReportStrings("en"); // the emailed report uses the published English
    const items = [];
    for (const section of sections) {
      for (const q of section.questions) {
        if (!(q.id in shown)) continue;
        const v = shown[q.id];
        const label = (x) => ENGLISH.opt[x] || ENGLISH.ratings[x] || (x === "na" ? "N/A" : String(x));
        const answer = Array.isArray(v) ? v.map(label).join(", ") : q.type === "text" ? v : label(v);
        const title = `${section.key}. ${ENGLISH.sections[section.key]}`;
        items.push({ section: title, question: ENGLISH.q[q.id] || q.id, answer });
        if (whyId(q.id) in shown) {
          items.push({ section: title, question: `${ENGLISH.q[q.id] || q.id} (${ENGLISH.ui.ratingWhy})`, answer: shown[whyId(q.id)] });
        }
      }
    }
    items.unshift({ section: "Interview", question: "Interview mode", answer: ENGLISH.ui.modes[ctx?.mode] || ctx?.mode });
    return items;
  };

  const submit = async () => {
    const allMissing = missingRequired(sections, answers, formCtx);
    if (allMissing.length) {
      setShowMissing(true);
      const first = allMissing[0].replace(/__why$/, "");
      const firstStage = STAGES.indexOf(sections.find((sec) => sec.questions.some((q) => q.id === first)).stage);
      setStage(firstStage);
      toast.error(s.ui.missing);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/beta-report/${sessionId}`, { answers: visibleAnswers(answers, formCtx), display: display(), client: clientInfo() });
      try { localStorage.removeItem(draftKey(sessionId)); } catch { /* blocked */ }
      toast.success(s.ui.submitted);
      navigate(`/report/${sessionId}`, { replace: true });
    } catch (ex) {
      if (ex?.response?.data?.detail?.code === "already_submitted") {
        navigate(`/report/${sessionId}`, { replace: true });
      } else {
        toast.error(s.ui.submitFail);
      }
    } finally {
      setBusy(false);
    }
  };

  if (failed) {
    return <div className="min-h-screen flex items-center justify-center"><button type="button" onClick={() => navigate("/")} className="rounded-full bg-[color:var(--brand)] text-white px-6 py-3">OK</button></div>;
  }
  if (!ctx || !wordingReady) return <div className="min-h-screen flex items-center justify-center text-[color:var(--text-secondary)]">…</div>;

  if (ctx.submitted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center">
        <p>{s.ui.alreadySent}</p>
        <button type="button" onClick={() => navigate(`/report/${sessionId}`)} className="rounded-full bg-[color:var(--brand)] text-white px-6 py-3">{s.ui.backToReport}</button>
      </div>
    );
  }

  const isLast = stage === STAGES.length - 1;
  return (
    <div className="min-h-screen bg-[color:var(--bg-base)]" data-testid="beta-report">
      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-8" ref={topRef}>
        <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand)]">{s.ui.eyebrow}</div>
        <h1 className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight">{s.ui.title}</h1>
        <p className="mt-3 text-sm text-[color:var(--text-secondary)] leading-relaxed">{s.ui.intro}</p>
        <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[color:var(--border-stone)] bg-white px-4 py-1.5 text-sm" data-testid="beta-report-mode">
          <span className="text-[color:var(--text-secondary)]">{s.ui.mode}:</span>
          <strong>{s.ui.modes[ctx.mode] || ctx.mode}</strong>
        </div>

        <ol className="mt-6 flex flex-wrap gap-2" aria-label={s.ui.progress} data-testid="beta-report-progress">
          {STAGES.map((st, i) => (
            <li key={st} className={`rounded-full px-3 py-1 text-xs border ${i === stage ? "border-[color:var(--brand)] bg-[color:var(--brand)] text-white" : i < stage ? "border-[color:var(--brand)] text-[color:var(--brand)]" : "border-[color:var(--border-stone)] text-[color:var(--text-secondary)]"}`}>
              {s.stages[st]}
            </li>
          ))}
        </ol>
        <div className="mt-2 text-xs text-[color:var(--text-secondary)]">{s.ui.stage(stage + 1, STAGES.length)}</div>

        {stageSections.map((section) => (
          <section key={section.key} className="mt-6 rounded-2xl border border-[color:var(--border-stone)] bg-white p-5 sm:p-6 space-y-5" data-testid={`beta-section-${section.key}`}>
            <h2 className="text-lg font-semibold">{s.sections[section.key]}</h2>
            {section.questions.filter((q) => isShown(q, answers, formCtx)).map((q) => (
              <Question key={q.id} q={q} s={s} value={answers[q.id]} onChange={(v) => set(q.id, v)} lang={lang}
                questionCount={ctx.question_count} highlight={showMissing && missing.includes(q.id)}
                why={answers[whyId(q.id)]} onWhy={(v) => set(whyId(q.id), v)}
                whyHighlight={showMissing && missing.includes(whyId(q.id))} />
            ))}
          </section>
        ))}

        <div className="mt-8 flex flex-wrap items-center gap-3">
          {stage > 0 && <button type="button" onClick={() => go(stage - 1)} className="rounded-full border border-[color:var(--border-stone)] bg-white px-5 py-2.5 text-sm">{s.ui.back}</button>}
          {!isLast && <button type="button" onClick={() => go(stage + 1)} className="rounded-full bg-[color:var(--brand)] text-white px-6 py-2.5 text-sm font-medium" data-testid="beta-report-next">{s.ui.next}</button>}
          {isLast && <button type="button" onClick={submit} disabled={busy} className="rounded-full bg-[color:var(--brand)] text-white px-6 py-2.5 text-sm font-medium disabled:opacity-50" data-testid="beta-report-submit">{busy ? s.ui.submitting : s.ui.submit}</button>}
          <button type="button" onClick={() => { saveDraft(answers, stage); navigate(`/report/${sessionId}`); }} className="ml-auto text-sm text-[color:var(--text-secondary)] underline" data-testid="beta-report-later">{s.ui.later}</button>
        </div>
      </main>
    </div>
  );
}

export function Question({ q, s, value, onChange, lang, questionCount, highlight, why, onWhy, whyHighlight }) {
  const label = s.q[q.id];
  const hint = s.ph?.[q.id] || undefined;
  const box = `rounded-xl p-3 ${highlight ? "ring-2 ring-[color:var(--score-bad)]" : ""}`;
  const head = (
    <div className="text-sm font-medium leading-snug">
      {label}
      {q.type !== "language" && (
        <span className="ml-2 text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">{q.required ? s.ui.required : s.ui.optional}</span>
      )}
    </div>
  );
  const pill = (active) => `rounded-full border px-3 py-1.5 text-sm ${active ? "border-[color:var(--brand)] bg-[color:var(--brand)] text-white" : "border-[color:var(--border-stone)] bg-white hover:border-[color:var(--brand)]"}`;

  if (q.type === "language") {
    return (
      <div className={box}>
        {head}
        <div className="mt-2 text-sm">{s.languages[lang] || lang} <span className="text-xs text-[color:var(--text-secondary)]">· {s.ui.languageAuto}</span></div>
      </div>
    );
  }
  if (isRating(q)) {
    const values = q.type === "rating_na" ? [...RATING_VALUES, "na"] : RATING_VALUES;
    const whyText = typeof why === "string" ? why : "";
    return (
      <div className={box} data-testid={`beta-q-${q.id}`}>
        {head}
        <div className="mt-2 flex flex-wrap gap-2">
          {values.map((v) => (
            <button key={v} type="button" onClick={() => onChange(v)} className={pill(value === v)} aria-pressed={value === v}>
              {v === "na" ? s.ui.na : s.ratings[v]}
            </button>
          ))}
        </div>
        {LOW_RATINGS.includes(value) && onWhy && (
          <div className={`mt-3 rounded-lg p-2 ${whyHighlight ? "ring-2 ring-[color:var(--score-bad)]" : ""}`} data-testid={`beta-why-${q.id}`}>
            <div className="text-sm font-medium">
              {s.ui.ratingWhy}
              <span className="ml-2 text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">{s.ui.required}</span>
            </div>
            <textarea value={whyText} onChange={(e) => onWhy(e.target.value.slice(0, TEXT_MAX))} rows={3} maxLength={TEXT_MAX}
              placeholder={s.ph?.[`${q.id}__why`] || s.ui.ratingWhyHint}
              className="mt-2 w-full rounded-lg border border-[color:var(--border-stone)] px-3 py-2 text-sm" />
          </div>
        )}
      </div>
    );
  }
  if (q.type === "choice" || q.type === "yesno") {
    const options = q.type === "yesno" ? ["yes", "no"] : q.options;
    return (
      <div className={box} data-testid={`beta-q-${q.id}`}>
        {head}
        <div className="mt-2 flex flex-wrap gap-2">
          {options.map((o) => (
            <button key={o} type="button" onClick={() => onChange(o)} className={pill(value === o)} aria-pressed={value === o}>{s.opt[o]}</button>
          ))}
        </div>
      </div>
    );
  }
  if (q.type === "multi") {
    const chosen = Array.isArray(value) ? value : [];
    const toggle = (o) => onChange(chosen.includes(o) ? chosen.filter((x) => x !== o) : [...chosen, o]);
    return (
      <div className={box} data-testid={`beta-q-${q.id}`}>
        {head}
        <div className="mt-2 flex flex-wrap gap-2">
          {q.options.map((o) => (
            <button key={o} type="button" onClick={() => toggle(o)} className={pill(chosen.includes(o))} aria-pressed={chosen.includes(o)}>{s.opt[o]}</button>
          ))}
        </div>
      </div>
    );
  }
  if (q.type === "question_number") {
    return (
      <div className={box} data-testid={`beta-q-${q.id}`}>
        <div className="text-sm font-medium">{s.ui.pickQuestion}</div>
        <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-2 rounded-lg border border-[color:var(--border-stone)] bg-white px-3 py-2 text-sm">
          <option value="">{s.ui.noQuestion}</option>
          {Array.from({ length: questionCount || 0 }, (_, i) => <option key={i} value={String(i + 1)}>{i + 1}</option>)}
        </select>
      </div>
    );
  }
  // text
  const text = typeof value === "string" ? value : "";
  return (
    <div className={box} data-testid={`beta-q-${q.id}`}>
      {head}
      <textarea value={text} onChange={(e) => onChange(e.target.value.slice(0, TEXT_MAX))} rows={4} maxLength={TEXT_MAX}
        placeholder={hint}
        className="mt-2 w-full rounded-lg border border-[color:var(--border-stone)] px-3 py-2 text-sm" />
      <div className="mt-1 text-[11px] text-[color:var(--text-secondary)]">{s.ui.charsLeft(TEXT_MAX - text.length)}</div>
    </div>
  );
}
